import type { LayoutPresetId } from "@gooner/shared";
import { LAYOUT_PRESETS, presetRects } from "@gooner/shared";
import { cx } from "../ui/primitives";
import { GoldChip } from "../ui/Gold";
import { PresetThumb } from "./PresetThumb";
import { Pager, usePaged } from "./Pager";

/** Default page: 3×3. The View panel passes 8/4 for its compact 4×2 instead. */
const PAGE_SIZE = 9;

/**
 * Paginated 3×3 grid of layout-preset buttons, each previewing its rect geometry.
 * Pass the target monitor's `aspect` so the previews are drawn in that screen's
 * real shape — on a 9:16 phone the vertical strips read as unusable slivers,
 * which is the point.
 *
 * `maxRegions` locks the presets past the caller's tier ceiling — dimmed with a GOLD
 * chip, but STILL CLICKABLE: a locked click reports through `onLocked` (which opens
 * the upsell) rather than dying in a disabled attribute, because a dead button is
 * frustration nothing ever measures. The component stays dumb — a number and a
 * callback, no session, no settings.
 */
export function PresetPalette({
  onApply,
  aspect,
  maxRegions = Number.POSITIVE_INFINITY,
  onLocked,
  pageSize = PAGE_SIZE,
  cols = 3,
}: {
  onApply: (id: LayoutPresetId) => void;
  aspect?: { w: number; h: number };
  maxRegions?: number;
  onLocked?: (id: LayoutPresetId) => void;
  /** Tiles per page — pair with `cols` (e.g. 8 with cols=4 for a compact 4×2). */
  pageSize?: number;
  /** Literal union, not a number into a template string: Tailwind only ships
   *  classes it can see, so the class name must appear verbatim below. */
  cols?: 3 | 4;
}) {
  const { page, pages, setPage, shown, pad } = usePaged(LAYOUT_PRESETS, pageSize);

  return (
    <div className="space-y-2">
      <div className={cx("grid gap-2", cols === 4 ? "grid-cols-4" : "grid-cols-3")}>
        {shown.map((p) => {
          const locked = presetRects(p.id).length > maxRegions;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => (locked ? onLocked?.(p.id) : onApply(p.id))}
              title={locked ? `${p.label} is a GoonLords Premium layout` : `Apply ${p.label} layout`}
              className={cx(
                "group rounded-lg border border-[var(--color-edge)] bg-[var(--color-ink)]/40 p-1.5 text-left transition-colors",
                locked
                  ? "opacity-60 hover:border-[var(--color-accent)]/60 hover:opacity-80"
                  : "hover:border-[var(--color-accent)]/60 hover:bg-[var(--color-panel-2)]",
              )}
            >
              <PresetThumb rects={presetRects(p.id)} aspect={aspect} />
              <span className="mt-1 flex items-center gap-1 truncate text-[11px] text-zinc-400 group-hover:text-zinc-200">
                <span className="truncate">{p.label}</span>
                {locked ? <GoldChip /> : null}
              </span>
            </button>
          );
        })}
        {Array.from({ length: pad }, (_, i) => (
          <div key={`pad-${i}`} aria-hidden className="invisible">
            <PresetThumb rects={[]} aspect={aspect} />
            <span className="mt-1 block text-[11px]">&nbsp;</span>
          </div>
        ))}
      </div>

      <Pager page={page} pages={pages} onPage={setPage} label="layouts" />
    </div>
  );
}
